import { useRef, useState, useEffect } from "react";
import { useNotifications } from "contexts/NotificationContext";
import NotificationSoundManager from "utils/NotificationSoundManager";
import { useGetNotificationPreferences } from "api/notificationPreferences";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Grow from "@mui/material/Grow";
import CircularProgress from "@mui/material/CircularProgress";
// import { openSnackbar } from "api/snackbar";
import { useNavigate } from "react-router-dom";

// project-imports
import MainCard from "components/MainCard";
import { headerIconButtonSx, V2 } from "layout/Dashboard/Header/headerV2Tokens";

// assets
import {
  TickCircle,
  NotificationCircle,
} from "iconsax-react";
import icons from "src/assets/icons";

// Notification sound
import notificationSound from "assets/audio/notification.mp3";

// Empty state component
const EmptyNotifications = () => (
  <Box sx={{ p: 4, textAlign: "center", fontFamily: V2.fontBody }}>
    <NotificationCircle
      size={40}
      variant="Bulk"
      style={{ opacity: 0.45, marginBottom: 10, color: V2.muted }}
    />
    <Typography
      sx={{
        fontFamily: V2.fontBody,
        fontWeight: 600,
        fontSize: "0.95rem",
        color: V2.ink,
      }}
    >
      Aucune notification
    </Typography>
    <Typography
      sx={{
        mt: 1,
        fontFamily: V2.fontBody,
        fontSize: "0.85rem",
        color: V2.muted,
      }}
    >
      Vous serez notifié ici des actualités importantes
    </Typography>
  </Box>
);

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "À l'instant";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `Il y a ${minutes} min${minutes > 1 ? "" : ""}`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `Il y a ${hours}h`;
  } else if (diffInSeconds < 172800) {
    return "Hier";
  } else {
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
    });
  }
};

const normalizeINPDisplayText = (text) => {
  if (typeof text !== "string" || !text) return text;
  // Keep legacy stored notifications readable with current terminology.
  return text.replace(/\bINP\b/gi, "INPE");
};

// Group notifications by date
const groupNotificationsByDate = (notifications) => {
  const groups = {};

  notifications.forEach((notification) => {
    const date = new Date(notification.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey;
    if (date.toDateString() === today.toDateString()) {
      groupKey = "Aujourd'hui";
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = "Hier";
    } else {
      groupKey = date.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
      });
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }

    groups[groupKey].push(notification);
  });

  // Sort notifications by date (newest first)
  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  });

  return groups;
};

// Main component
export default function NotificationMedicalePage() {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  // use app snackbar helper
  const navigate = useNavigate();
  const { preferences: notifPrefs } = useGetNotificationPreferences();

  // Use our WebSocket notification context
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
    refreshNotifications,
    shouldPlaySound,
    resetShouldPlaySound,
  } = useNotifications();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [, setLoading] = useState(false);
  const [refreshing] = useState(false);
  // const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isNavigating, setIsNavigating] = useState(false);

  // Initialize NotificationSoundManager
  useEffect(() => {
    NotificationSoundManager.initialize(notificationSound);
  }, []);

  // Watch for shouldPlaySound flag from context and play sound only when it's true
  useEffect(() => {
    if (shouldPlaySound && !isNavigating && !open) {

      // Play sound only if enabled in preferences (default true)
      const canPlaySound =
        notifPrefs?.sound_notifications !== undefined
          ? notifPrefs.sound_notifications
          : true;
      if (canPlaySound) {
        NotificationSoundManager.playSound();
      }

      // Reset the flag after playing sound (or deciding not to)
      resetShouldPlaySound();
    }
  }, [shouldPlaySound, isNavigating, open, notifPrefs, resetShouldPlaySound]);

  // Mark all notifications as read
  const handleMarkAllAsRead = () => {
    markAllNotificationsAsRead();
  };

  // Update lastUpdate when notifications change
  // useEffect(() => {
  //   if (notifications.length > 0) {
  //     setLastUpdate(new Date());
  //   }
  // }, [notifications]);

  // Fetch notifications when popup opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      refreshNotifications();
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [open, refreshNotifications]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // Reset isNavigating flag after navigation
  useEffect(() => {
    return () => {
      if (isNavigating) {
        setIsNavigating(false);
      }
    };
  }, [isNavigating]);

  // Define function to handle click on notification
  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      markNotificationAsRead(notification.id);
    }

    // Set isNavigating to true to prevent sound from playing
    setIsNavigating(true);

    // Handle INP verification notifications - redirect to home
    if (
      notification.notification_type === "INP_VERIFICATION" ||
      (notification.related_object &&
        notification.related_object.type === "inp_verification")
    ) {
      navigate("/");

    }
    // Contribution suggestions (staff queue vs user decision)
    else if (
      notification.related_object?.type === "contribuer" &&
      notification.related_object.kind
    ) {
      const ro = notification.related_object;
      const kindToTab = {
        formation: 0,
        expert: 1,
        institution: 2,
        event: 3,
        document: 4,
        question: 5,
        medicament: 6,
      };
      if (notification.notification_type === "CONTRIBUTION_SUBMITTED") {
        navigate("/admin/notifications?tab=1");
      } else {
        const tab = kindToTab[ro.kind] ?? 0;
        const inner =
          notification.notification_type === "CONTRIBUTION_DECISION" ? "1" : "0";
        navigate(`/contribuer?tab=${tab}&inner=${inner}`);
      }
    }
    // Handle navigation based on notification type and related object
    else if (notification.related_object) {
      const { type, id } = notification.related_object;
      // Navigate to the appropriate page based on related object type
      if (type && id) {
        navigate(`/${type}/${id}`);

      }
    }

    // Close the notification panel
    handleClose({ target: document.body });
  };

  const getPriorityChipProps = (type) => {
    switch (type?.toLowerCase()) {
      case "comment_validated":
        return {
          label: "Validé",
          color: "#22bb33",
        };
      case "report_resolved":
        return {
          label: "Résolu",
          color: "#22bb33",
        };
      case "comment_deleted":
        return {
          label: "Refusé",
          color: "#f0ad4e",
        };
      case "report_dismissed":
        return {
          label: "Rejeté",
          color: "#f0ad4e",
        };
      case "urgent":
        return {
          label: "Urgent",
          color: "#bb2124",
        };
      case "publication":
        return {
          label: "Info",
          color: "#5bc0de",
        };
      case "event":
        return {
          label: "Événement",
          color: "warning",
        };
      case "contribution_submitted":
        return {
          label: "Contribution",
          color: "#5bc0de",
        };
      case "contribution_decision":
        return {
          label: "Réponse",
          color: "#22bb33",
        };
      default:
        return {
          label: "Notification",
          color: "primary",
        };
    }
  };

  // Get notification variant for toast message
  // const getNotificationVariant = (type) => {
  //   switch (type?.toLowerCase()) {
  //     case "comment_validated":
  //       return "success";
  //     case "report_resolved":
  //       return "success";
  //     case "comment_deleted":
  //       return "warning";
  //     case "report_rejected":
  //       return "warning";
  //     case "urgent":
  //       return "error";
  //     default:
  //       return "info";
  //   }
  // };

  // Group notifications for display
  const groupedNotifications = groupNotificationsByDate(notifications);
  const hasUnread = notifications.some((notification) => !notification.is_read);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5, position: "relative" }}>
      <Tooltip title="Notifications">
        <IconButton
          color="secondary"
          variant="text"
          aria-label="ouvrir les notifications médicales"
          ref={anchorRef}
          aria-controls={open ? "notifications-medicales-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          size="large"
          sx={headerIconButtonSx(open)}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{
              "& .MuiBadge-badge": {
                top: 2,
                right: 4,
              },
            }}
          >
            <img
              src={icons.notifs}
              alt="Notifications"
              width="20"
              height="20"
            />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Connection status indicator */}
      <Tooltip
        title={
          isConnected
            ? "Connecté aux notifications en temps réel"
            : "Déconnecté du serveur de notifications"
        }
      >
        <Box
          sx={{
            position: "absolute",
            width: 8,
            height: 8,
            bgcolor: isConnected ? "success.main" : "error.main",
            borderRadius: "50%",
            border: "1px solid",
            borderColor: "background.paper",
            top: 8,
            right: 8,
          }}
        />
      </Tooltip>

      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            { name: "offset", options: { offset: [matchesXs ? -5 : 0, 9] } },
          ],
        }}
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top right" }}>
            <Paper
              sx={{
                boxShadow: "0 12px 40px rgba(15, 23, 32, 0.12)",
                borderRadius: 'var(--brand-radius-md)',
                border: `1px solid ${V2.line}`,
                width: "100%",
                minWidth: 320,
                maxWidth: 420,
                overflow: "hidden",
                bgcolor: V2.surfaceSolid,
                fontFamily: V2.fontBody,
                [theme.breakpoints.down("md")]: { maxWidth: 340 },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  elevation={0}
                  border={false}
                  content={false}
                  sx={{ borderRadius: 'var(--brand-radius-md)', bgcolor: V2.surfaceSolid }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: `1px solid ${V2.line}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        sx={{
                          fontFamily: V2.fontDisplay,
                          fontWeight: 600,
                          color: V2.ink,
                          fontSize: "1.05rem",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Notifications
                      </Typography>
                      {hasUnread && (
                        <Tooltip title="Tout marquer comme lu">
                          <Button
                            size="small"
                            onClick={handleMarkAllAsRead}
                            startIcon={<TickCircle size={16} />}
                            sx={{
                              color: V2.accent,
                              fontFamily: V2.fontBody,
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: 'var(--brand-radius-md)',
                              "&:hover": { bgcolor: V2.accentSoft },
                            }}
                          >
                            Tout lu
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      height: "auto",
                      maxHeight: 450,
                      overflow: "auto",
                      position: "relative",
                    }}
                  >
                    {notifications.length === 0 ? (
                      <EmptyNotifications />
                    ) : (
                      Object.entries(groupedNotifications).map(
                        ([date, dateNotifications]) => (
                          <Box sx={{ p: 1 }} key={date}>
                            <Typography
                              sx={{
                                px: 1.5,
                                pt: 0.75,
                                pb: 0.5,
                                fontFamily: V2.fontBody,
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: V2.muted,
                              }}
                            >
                              {date}
                            </Typography>
                            <List component="nav" sx={{ p: 0 }}>
                              {dateNotifications.map((notification) => (
                                <ListItemButton
                                  key={notification.id}
                                  sx={{
                                    px: 1.5,
                                    py: 1.25,
                                    mb: 0.35,
                                    borderRadius: 'var(--brand-radius-md)',
                                    border: `1px solid ${
                                      !notification.is_read
                                        ? V2.accentBorder
                                        : "transparent"
                                    }`,
                                    bgcolor: !notification.is_read
                                      ? V2.accentSoft
                                      : "transparent",
                                    "&:hover": {
                                      bgcolor: V2.accentSoft,
                                      borderColor: V2.accentBorder,
                                    },
                                  }}
                                  onClick={() =>
                                    handleNotificationClick(notification)
                                  }
                                >
                                  <ListItemText
                                    primary={
                                      <Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.875rem",
                                            fontFamily: V2.fontBody,
                                            color: V2.ink,
                                            fontWeight: !notification.is_read
                                              ? 600
                                              : 500,
                                          }}
                                        >
                                          {normalizeINPDisplayText(
                                            notification.title,
                                          )}
                                        </Typography>
                                        {(notification.message ||
                                          notification?.related_data
                                            ?.admin_explanation) && (
                                          <Typography
                                            sx={{
                                              fontSize: "0.8rem",
                                              fontFamily: V2.fontBody,
                                              color: V2.muted,
                                              mt: 0.25,
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {normalizeINPDisplayText(
                                              notification?.related_data
                                                ?.admin_explanation ||
                                                notification.message,
                                            )}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                    secondary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          width: "100%",
                                          alignItems: "flex-end",
                                          justifyContent: "space-between",
                                          gap: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontFamily: V2.fontBody,
                                            fontSize: "0.7rem",
                                            color: V2.muted,
                                          }}
                                        >
                                          {formatTime(notification.created_at)}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontFamily: V2.fontBody,
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            color: `${getPriorityChipProps(notification.notification_type).color}`,
                                          }}
                                        >
                                          {
                                            getPriorityChipProps(
                                              notification.notification_type,
                                            ).label
                                          }
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Box>
                        ),
                      )
                    )}

                    {refreshing && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.72)",
                          zIndex: 2,
                        }}
                      >
                        <CircularProgress size={24} sx={{ color: V2.accent }} />
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: V2.line }} />
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}
